import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { usePopup } from "../../../contexts/PopupContext";
import ShareModal from "../ShareModal";
import SharedWithMeSection from "../SharedWithMeSection";
import { Plus, Trash2, Pencil, Share2, FileText, Lock } from "lucide-react";

import {
  createReport,
  listReports,
  deleteReport,
  renameReport,
} from "../../../../api/reports";
import { usePortalTarget } from "./sidebarUtils";
import { useLayout } from "../../../contexts/LayoutContext";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const getSharedReportId = (item) =>
  item?.report_id || item?.document_id || item?.id || "";

const ReportsSidebarPortal = () => {
  const navigate = useNavigate();
  const portalTarget = usePortalTarget("reports-portal-slot");
  const { currentAppPage, leftOpen } = useLayout();
  const [reports, setReports] = useState([]);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [pendingDeleteReportId, setPendingDeleteReportId] = useState(null);
  const [renamingReportId, setRenamingReportId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  // ── User reports ─────────────────────────────────────────────────────────
  const loadReports = useCallback(async () => {
    try {
      const res = await listReports();
      setReports(res?.data?.reports || []);
    } catch (e) {
      console.error("Error loading reports:", e);
      setReports([]);
    }
  }, []);

  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    register(
      "/reports",
      reports.map((r) => ({
        id: r.report_id,
        label: r.title || "Untitled Report",
        keywords: [],
        icon: FileText,
        event: {
          navigateTo: "/reports",
          name: "sidebar:report-selected",
          detail: { reportId: r.report_id },
        },
      })),
    );
    return () => unregister("/reports");
  }, [reports, register, unregister]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const refreshTimerRef = useRef(null);
  useEffect(() => {
    const handler = () => {
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = setTimeout(() => loadReports(), 800);
    };
    window.addEventListener("sidebar:refresh-reports", handler);
    return () => {
      window.removeEventListener("sidebar:refresh-reports", handler);
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    };
  }, [loadReports]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.reportId !== undefined)
        setCurrentReportId(e.detail.reportId);
    };
    window.addEventListener("sidebar:select-report", handler);
    return () => window.removeEventListener("sidebar:select-report", handler);
  }, []);

  useEffect(() => {
    if (currentAppPage !== "/reports" && currentReportId !== null) {
      setCurrentReportId(null);
    }
  }, [currentAppPage, currentReportId]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section && e.detail.section !== "reports")
        setCurrentReportId(null);
    };
    window.addEventListener("sidebar:active-section-changed", handler);
    return () =>
      window.removeEventListener("sidebar:active-section-changed", handler);
  }, []);

  // ── Breadcrumb ───────────────────────────────────────────────────────────
  const emitReportsBreadcrumb = useCallback(() => {
    if (currentAppPage !== "/reports") return;
    const activeReport = reports.find((r) => r.report_id === currentReportId);
    window.dispatchEvent(
      new CustomEvent("breadcrumb:update", {
        detail: {
          section: "/reports",
          subLabel: activeReport
            ? activeReport.title || "Untitled Report"
            : null,
          subIcon: null,
          hasAdd: true,
          addKind: "reports",
          addLabel: "New report",
        },
      }),
    );
  }, [currentReportId, reports, currentAppPage]);

  useEffect(() => {
    emitReportsBreadcrumb();
  }, [emitReportsBreadcrumb, leftOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section === "/reports") emitReportsBreadcrumb();
    };
    window.addEventListener("breadcrumb:request", handler);
    return () => window.removeEventListener("breadcrumb:request", handler);
  }, [emitReportsBreadcrumb]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.section !== "/reports") return;

      (async () => {
        try {
          const res = await createReport();
          const reportId = res?.data?.report_id;
          if (!reportId) return;

          setCurrentReportId(reportId);
          if (currentAppPage !== "/reports") navigate("/reports");
          window.dispatchEvent(
            new CustomEvent("sidebar:new-report", {
              detail: { reportId },
            }),
          );
          loadReports();
        } catch (err) {
          console.error("Error creating report:", err);
        }
      })();
    };
    window.addEventListener("breadcrumb:add", handler);
    return () => window.removeEventListener("breadcrumb:add", handler);
  }, [currentAppPage, navigate, loadReports]);

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleNew = async () => {
    try {
      const res = await createReport();
      const reportId = res?.data?.report_id;
      if (!reportId) return;

      setCurrentReportId(reportId);
      if (currentAppPage !== "/reports") navigate("/reports");
      window.dispatchEvent(
        new CustomEvent("sidebar:new-report", {
          detail: { reportId },
        }),
      );
      loadReports();
    } catch (e) {
      console.error("Error creating report:", e);
    }
  };

  const handleSelect = (reportId) => {
    setCurrentReportId(reportId);

    if (currentAppPage !== "/reports") navigate("/reports");

    window.dispatchEvent(
      new CustomEvent("sidebar:report-selected", { detail: { reportId } }),
    );
    window.dispatchEvent(
      new CustomEvent("sidebar:active-section-changed", {
        detail: { section: "reports" },
      }),
    );
  };

  const confirmDeleteReport = async (reportId) => {
    try {
      await deleteReport(reportId);
      if (currentReportId === reportId) setCurrentReportId(null);
      loadReports();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } catch (err) {
      console.error("Delete report failed:", err);
    } finally {
      setPendingDeleteReportId(null);
    }
  };

  const commitRename = async (reportId) => {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenamingReportId(null);
      return;
    }
    try {
      await renameReport(reportId, trimmed);
      loadReports();
      window.dispatchEvent(new CustomEvent("sidebar:refresh-reports"));
    } catch (err) {
      console.error("Rename report failed:", err);
    } finally {
      setRenamingReportId(null);
    }
  };

  useContextMenu("report-item", [
    {
      label: "Share",
      icon: Share2,
      action: (payload) => {
        setPopUpComponent(
          <ShareModal
            onClose={() => setIsPopUpOpen(false)}
            contentType="report"
            documentId={payload.report_id}
            itemName={payload.title || "Untitled Report"}
          />,
        );
        setIsPopUpOpen(true);
      },
    },
    {
      label: "Rename",
      icon: Pencil,
      action: (payload) => {
        setRenamingReportId(payload.report_id);
        setRenameValue(payload.title || "Untitled Report");
      },
    },
    {
      label: "Delete",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        setPendingDeleteReportId(payload.report_id);
      },
    },
  ]);

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  const content = (
    <div
      className="flex flex-col gap-y-px"
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Default Report (always present) ── */}
      {!isInNavbar && (
        <button
          onClick={() => handleSelect("default")}
          className={[
            "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-colors flex items-center gap-x-1.5",
            currentReportId === "default"
              ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
              : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
          ].join(" ")}
        >
          <Lock className="h-3 w-3 shrink-0 opacity-60" strokeWidth={2} />
          <span className="truncate">Default Report</span>
        </button>
      )}

      {/* ── New report button ── */}
      {!isInNavbar && (
        <button
          onClick={handleNew}
          className="group w-full flex items-center gap-x-2 rounded-md px-2 py-1.5 text-[12px] text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800/60 transition-colors"
          title="New report"
        >
          <Plus className="h-3 w-3 shrink-0" strokeWidth={2.5} />
          <span>New report</span>
        </button>
      )}

      {/* ── User reports ── */}
      {reports.map((r) => {
        const isActive = r.report_id === currentReportId;
        const isRenaming = renamingReportId === r.report_id;
        const isPendingDelete = pendingDeleteReportId === r.report_id;

        return (
          <div
            key={r.report_id}
            data-context-menu="report-item"
            data-context-payload={JSON.stringify(r)}
          >
            {isRenaming ? (
              <div className="flex items-center gap-x-1 px-2 py-1">
                <input
                  autoFocus
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") commitRename(r.report_id);
                    if (e.key === "Escape") setRenamingReportId(null);
                  }}
                  onBlur={() => commitRename(r.report_id)}
                  className="flex-1 min-w-0 bg-transparent text-[12px] text-slate-700 dark:text-gray-200 border border-[#0EA5E9]/30 rounded px-1.5 py-1 outline-none focus:border-[#0EA5E9]/60"
                />
              </div>
            ) : (
              <button
                onClick={() => handleSelect(r.report_id)}
                title={r.title}
                className={[
                  "w-full text-left rounded-md px-2 py-1.5 text-[12px] truncate transition-colors flex items-center gap-x-1.5",
                  isActive
                    ? "text-[#0EA5E9] bg-[#0EA5E9]/[0.08] font-medium"
                    : "text-zinc-500 dark:text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800/40",
                ].join(" ")}
              >
                <span className="truncate">{r.title || "Untitled Report"}</span>
              </button>
            )}

            <div
              className={[
                "overflow-hidden transition-all duration-200 ease-in-out",
                isPendingDelete
                  ? "max-h-[80px] opacity-100 mt-0.5 mb-0.5"
                  : "max-h-0 opacity-0",
              ].join(" ")}
            >
              {isPendingDelete && (
                <div className="flex items-center gap-x-2 py-1 px-1">
                  <span className="text-[11px] text-slate-500 dark:text-gray-400">
                    Delete?
                  </span>
                  <button
                    onClick={() => setPendingDeleteReportId(null)}
                    className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => confirmDeleteReport(r.report_id)}
                    className="text-[11px] px-2 py-0.5 rounded border border-red-300 dark:border-red-500/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                  >
                    Confirm
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <SharedWithMeSection
        contentType="reports"
        activeItemId={currentReportId}
        getItemId={getSharedReportId}
        onSelect={(item) => {
          const reportId = getSharedReportId(item);
          handleSelect(reportId);
        }}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default ReportsSidebarPortal;
